import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { EthnicService } from 'src/app/services/ethnic/ethnic.service';
import { Observable, forkJoin } from 'rxjs';
import { EthnicGroup } from 'src/app/models/data/ethnic-group.model';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { DropdownService } from 'src/app/services/dropdown/dropdown.service';
import { CompletionQuestionsService } from 'src/app/services/completion-questions/completion-questions.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AddEthnicGroupComponent } from '../add-ethnic-group/add-ethnic-group.component';
import { TableSortComponent } from 'src/app/components/app/table-sort/table-sort.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { NumberFormatDirective } from 'src/app/directives/number-format.directive';
import { NumberFormatPipe } from 'src/app/pipes/number-format.pipe';
import { NumberFormatter } from 'src/app/tools/number-formatter';
import { max10DigitsValidator } from 'src/app/validators/max-digits.validator';
import { AutoSaveService } from 'src/app/services/auto-save/auto-save.service';

@Component({
  selector: 'app-ethnic-groups',
  templateUrl: './ethnic-groups.component.html',
  styleUrls: ['./ethnic-groups.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AddEthnicGroupComponent,
    ReactiveFormsModule,
    FormsModule,
    TableSortComponent,
    NgxPaginationModule,
    NumberFormatDirective,
    NumberFormatPipe,
  ],
})
export class EthnicGroupsComponent implements OnInit, OnDestroy {
  headers!: string[];
  p: number = 1;
  id: any;
  editState = false;
  ethnics: EthnicGroup[] = [];
  allEthnics: EthnicGroup[] = [];
  itemToEdit!: EthnicGroup;
  ethnicGroupForm!: FormGroup;
  submitted!: boolean;
  type!: string;
  numberPattern: string = '^[0-9]+$';
  decimalPattern: string = '^[0-9]+(\.[0-9]{1,2})?$'; // Positive decimals only
  activeHeaderSort: string = '';
  allCurrentEthnicGroupsReviewed: boolean = false;
  allFutureEthnicGroupsReviewed: boolean = false;
  fieldErrors: { [fieldName: string]: string | null } = {};
  notApplicable: boolean = false;
  private notApplicableInitializedForQuestion: number | null = null;

  constructor(
    private router: Router,
    private ethnicService: EthnicService,
    private dropdownService: DropdownService,
    private completionQuestionsService: CompletionQuestionsService,
    private autoSaveService: AutoSaveService,
  ) { }

  ngOnInit(): void {
    this.restorePaginationState();
    this.notApplicableInitializedForQuestion = null; // Reset flag on component init

    this.ethnicGroupForm = new FormGroup({
      Name: new FormControl(null, [Validators.required]),
      Language: new FormControl(null, []),
      Location: new FormControl(null, []),
      Action: new FormControl('A', [Validators.required]),
      Status: new FormControl(null, [Validators.required]),
      Workers: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.numberPattern),
        max10DigitsValidator(),
      ]),
      WorkersBenefitted: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.numberPattern),
        max10DigitsValidator(),
      ]),
      Churches: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.numberPattern),
        max10DigitsValidator(),
      ]),
      Believers: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.numberPattern),
        max10DigitsValidator(),
      ]),
      AmountPerWorker: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.decimalPattern),
        max10DigitsValidator(),
      ]),
      Year: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.numberPattern),
        max10DigitsValidator(),
      ]),
    });
    this.getData();
  }

  private handleRouteChange(): void {
    if (this.type) {
      this.storePaginationState();
    }
    this.restorePaginationState();
  }

  private storePaginationState(): void {
    const routeKey = this.router.url === '/current-ethnic-groups' ? 'current' : 'future';
    sessionStorage.setItem(`ethnicGroupsPage_${routeKey}`, this.p.toString());
  }

  private restorePaginationState(): void {
    const routeKey = this.router.url === '/current-ethnic-groups' ? 'current' : 'future';
    const savedPage = sessionStorage.getItem(`ethnicGroupsPage_${routeKey}`);
    if (savedPage) {
      this.p = parseInt(savedPage, 10);
    }
  }

  private clearPaginationState(): void {
    sessionStorage.removeItem('ethnicGroupsPage_current');
    sessionStorage.removeItem('ethnicGroupsPage_future');
  }

  onPageChange(page: number): void {
    this.p = page;
    this.storePaginationState();
  }

  editItem(ethnic: EthnicGroup) {
    this.editState = !this.editState;
    this.itemToEdit = ethnic;

    // Clear any existing field errors when starting to edit
    this.fieldErrors = {};

    // Set form values when editing
    this.ethnicGroupForm.patchValue({
      Name: ethnic.Name,
      Language: ethnic.Language || null,
      Location: ethnic.Location || null,
      Workers: ethnic.Workers,
      WorkersBenefitted: ethnic.WorkersBenefitted,
      Churches: ethnic.Churches,
      Believers: ethnic.Believers,
      AmountPerWorker: ethnic.AmountPerWorker,
      Year: ethnic.Year,
      Action: 'E',
      Status: this.router.url == '/current-ethnic-groups' ? 'C' : 'F',
    });

    // Trigger validation
    this.ethnicGroupForm.updateValueAndValidity();

    // Initialize auto-save for all editable fields
    this.initializeAutoSaveForEditing(ethnic);
  }

  confirmItem(ethnic: EthnicGroup) {
    ethnic.Action = 'R';
    this.ethnicService.updateEthnicGroup(
      ethnic,
      ethnic.EthnicGroupID,
      this.router.url == '/current-ethnic-groups' ? 'Current' : 'future',
    ).subscribe(() => {
      // Refresh completion status from API
      this.dropdownService.setPageCompletionStatuses();
      this.storePaginationState();
      const currentRoute = this.router.url;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([currentRoute]);
      });
    });
  }

  onDelete(ethnic: EthnicGroup) {
    if (confirm('Are you sure?')) {
      this.ethnicService.deleteEthnic(
        ethnic,
        this.router.url == '/current-ethnic-groups' ? 'current' : 'future',
      );
      // Refresh completion status from API
      this.dropdownService.setPageCompletionStatuses();
    }
  }

  cancelDelete() {
    this.ethnicService.getEthnicOb().subscribe((ethnics) => {
      this.ethnics = ethnics;
    });
    this.editState = !this.editState;
    this.submitted = false;
    this.ethnicGroupForm.reset();
    // Clear any existing field errors when canceling edit
    this.fieldErrors = {};
    this.getData();
  }

  getData() {
    this.ethnicService.getAllEthnicGroups();
    this.ethnicService.getAllEthnicOb().subscribe((allEthnics) => {
      this.allEthnics = allEthnics;
      this.checkActions();
    });

    const previousType = this.type;

    if (this.router.url == '/current-ethnic-groups') {
      this.type = 'Current';
      this.headers = [
        'Ethnic Group',
        'Language',
        'Location',
        'Status',
        'Workers',
        'CAM Supported Workers',
        'Churches',
        'Believers',
        'Year Begun',
        'Actions',
      ];
      this.ethnicService.getEthnicGroups('current');
      this.ethnicService.getEthnicOb().subscribe((ethnics) => {
        this.ethnics = ethnics;
        // Initialize checkbox state from backend when data is loaded
        // Only initialize once per question to prevent unnecessary API calls
        if (this.notApplicableInitializedForQuestion !== 43) {
          this.notApplicableInitializedForQuestion = 43;
          this.initializeNotApplicableState(43);
        }
      });
    } else {
      this.type = 'Future';
      this.headers = [
        'Ethnic Group',
        'Language',
        'Location',
        'Status',
        'Workers',
        'Monthly Cost($US) per Worker',
        'Year to Begin',
        'Actions',
      ];
      this.ethnicService.getEthnicGroups('future');
      this.ethnicService.getEthnicOb().subscribe((ethnics) => {
        this.ethnics = ethnics;
        // Initialize checkbox state from backend when data is loaded
        // Only initialize once per question to prevent unnecessary API calls
        if (this.notApplicableInitializedForQuestion !== 44) {
          this.notApplicableInitializedForQuestion = 44;
          this.initializeNotApplicableState(44);
        }
      });
    }

    if (previousType && previousType !== this.type) {
      this.handleRouteChange();
    }
  }

  initializeNotApplicableState(questionNum: number) {
    this.completionQuestionsService.getQuestion(questionNum).subscribe({
      next: (question) => {
        if (question) {
          console.log(`Loading notApplicable state for question ${questionNum}:`, question.notApplicable);
          // Always update from backend - it's the source of truth
          if (question.notApplicable === true) {
            this.notApplicable = true;
            console.log(`Checkbox set to checked for question ${questionNum}`);
          } else {
            // Set to false if null, false, or undefined
            this.notApplicable = false;
            console.log(`Checkbox set to unchecked for question ${questionNum}`);
          }
        } else {
          console.warn(`Question ${questionNum} not found, preserving current checkbox state:`, this.notApplicable);
        }
      },
      error: (error) => {
        console.warn(`Failed to load notApplicable state for question ${questionNum}, preserving current state:`, this.notApplicable, error);
      }
    });
  }

  onNotApplicableChange() {
    const questionNum = this.router.url == '/current-ethnic-groups' ? 43 : 44;
    this.completionQuestionsService.updateNotApplicable(questionNum, this.notApplicable)
      .subscribe({
        next: (result) => {
          console.log(`NotApplicable updated to ${this.notApplicable} for question ${questionNum}`, result);
          // Don't re-fetch the state - trust the local state we just set
          // Re-fetching can cause race conditions where the backend hasn't updated yet
        },
        error: (error) => {
          console.error(`Failed to update NotApplicable for question ${questionNum}:`, error);
          // If update fails, revert the checkbox state
          this.notApplicable = !this.notApplicable;
        }
      });
  }

  canNavigate(): boolean {
    if (this.ethnics.length === 0) {
      return this.notApplicable;
    }
    // Existing logic for when groups exist
    if (this.router.url == '/current-ethnic-groups') {
      return this.allCurrentEthnicGroupsReviewed;
    } else {
      return this.allFutureEthnicGroupsReviewed;
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.router.url == '/current-ethnic-groups') {
      this.ethnicGroupForm.patchValue({
        Name: this.itemToEdit.Name,
        AmountPerWorker: this.itemToEdit.AmountPerWorker,
        Status: 'C',
        Action: 'E',
      });
      if (!this.ethnicGroupForm.valid) {
        return;
      }
      console.log(this.ethnicGroupForm.value);

      // Clean the form data to ensure raw numbers are sent to API
      const cleanedData = this.cleanFormData(this.ethnicGroupForm.value);
      this.ethnicService.updateEthnicGroup(
        cleanedData,
        this.itemToEdit.EthnicGroupID,
        'Current',
      ).subscribe(() => {
        // Refresh completion status from API
        this.dropdownService.setPageCompletionStatuses();
        this.storePaginationState();
        const currentRoute = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentRoute]);
        });
      });
    } else {
      this.ethnicGroupForm.patchValue({
        Name: this.itemToEdit.Name,
        WorkersBenefitted: this.itemToEdit.WorkersBenefitted,
        Churches: this.itemToEdit.Churches,
        Believers: this.itemToEdit.Believers,
        Status: 'F',
        Action: 'E',
      });
      if (!this.ethnicGroupForm.valid) {
        return;
      }

      // Clean the form data to ensure raw numbers are sent to API
      const cleanedData = this.cleanFormData(this.ethnicGroupForm.value);
      this.ethnicService.updateEthnicGroup(
        cleanedData,
        this.itemToEdit.EthnicGroupID,
        'future',
      ).subscribe(() => {
        // Refresh completion status from API
        this.dropdownService.setPageCompletionStatuses();
        this.storePaginationState();
        const currentRoute = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentRoute]);
        });
      });
    }
  }

  makeCurrent(ethnic: EthnicGroup): void {
    ethnic.Status = 'C';
    this.ethnicService.updateEthnicGroup(
      ethnic,
      ethnic.EthnicGroupID,
      'future',
    ).subscribe(() => {
      // Refresh completion status from API
      this.dropdownService.setPageCompletionStatuses();
    });
  }

  onNavigate() {
    console.log('onNavigate() called');
    const currentState = {
      route: this.router.url,
      type: this.type,
      ethnicsLength: this.ethnics.length,
      allCurrentReviewed: this.allCurrentEthnicGroupsReviewed,
      allFutureReviewed: this.allFutureEthnicGroupsReviewed
    };
    console.log('Current state:', currentState);

    // OLD LOGIC: Manual completion status setting - now handled by API
    // this.dropdownService.setEthnicTableToComplete(
    //   this.allCurrentEthnicGroupsReviewed,
    //   this.allFutureEthnicGroupsReviewed,
    // );

    // Answer completion questions if criteria are met
    // Phase II: Require checkbox to be checked when no groups exist
    if (this.router.url == '/current-ethnic-groups') {
      if (this.ethnics.length === 0 && !this.notApplicable) {
        // Block navigation if no groups and checkbox not checked
        console.log('Cannot navigate: No ethnic groups and checkbox not checked');
        return;
      }
      if ((this.ethnics.length === 0 && this.notApplicable) || (this.ethnics.length > 0 && this.allCurrentEthnicGroupsReviewed)) {
        console.log('Updating completion question 43 for Current Ethnic Groups');
        // Explicitly pass notApplicable state to ensure it's persisted
        const notApplicableValue = this.ethnics.length === 0 ? this.notApplicable : undefined;
        this.completionQuestionsService.updateCompletionQuestion(43, true, notApplicableValue).subscribe(() => {
          console.log('Completion question 43 updated successfully');
          // Add a small delay to ensure backend has processed the update
          setTimeout(() => {
            console.log('Refreshing page completion statuses');
            this.dropdownService.setPageCompletionStatuses();
            // Navigate after completion question is updated
            this.clearPaginationState();
            this.router.navigate(['/future-ethnic-groups']);
          }, 500);
        });
        return;
      }
    } else if (this.router.url == '/future-ethnic-groups') {
      if (this.ethnics.length === 0 && !this.notApplicable) {
        // Block navigation if no groups and checkbox not checked
        console.log('Cannot navigate: No ethnic groups and checkbox not checked');
        return;
      }
      if ((this.ethnics.length === 0 && this.notApplicable) || (this.ethnics.length > 0 && this.allFutureEthnicGroupsReviewed)) {
        console.log('Updating completion question 44 for Future Ethnic Groups');
        // Explicitly pass notApplicable state to ensure it's persisted
        const notApplicableValue = this.ethnics.length === 0 ? this.notApplicable : undefined;
        this.completionQuestionsService.updateCompletionQuestion(44, true, notApplicableValue).subscribe(() => {
          console.log('Completion question 44 updated successfully');
          // Add a small delay to ensure backend has processed the update
          setTimeout(() => {
            console.log('Refreshing page completion statuses');
            this.dropdownService.setPageCompletionStatuses();
            // Navigate after completion question is updated
            this.clearPaginationState();
            this.router.navigate(['/activities']);
          }, 500);
        });
        return;
      }
    }

    console.log('Completion question not updated:', {
      route: this.router.url,
      ethnicsLength: this.ethnics.length,
      allCurrentReviewed: this.allCurrentEthnicGroupsReviewed,
      allFutureReviewed: this.allFutureEthnicGroupsReviewed,
      notApplicable: this.notApplicable
    });
    // Navigate immediately if no completion question to update
    this.clearPaginationState();
    if (this.router.url == '/current-ethnic-groups') {
      this.router.navigate(['/future-ethnic-groups']);
    } else {
      this.router.navigate(['/activities']);
    }
  }

  sortTable(sortDesc: boolean, columnHeader: string): void {
    this.activeHeaderSort = columnHeader;

    switch (columnHeader) {
      case 'Ethnic Group':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return ('' + x.Name).localeCompare(y.Name);
          else return ('' + y.Name).localeCompare(x.Name);
        });
        break;
      case 'Language':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return (x.Language || '').localeCompare(y.Language || '');
          else return (y.Language || '').localeCompare(x.Language || '');
        });
        break;
      case 'Location':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return (x.Location || '').localeCompare(y.Location || '');
          else return (y.Location || '').localeCompare(x.Location || '');
        });
        break;
      case 'Status':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return ('' + x.Action).localeCompare(y.Action);
          else return ('' + y.Action).localeCompare(x.Action);
        });
        break;
      case 'Workers':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.Workers - y.Workers;
          else return y.Workers - x.Workers;
        });
        break;
      case 'CAM Supported Workers':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.WorkersBenefitted - y.WorkersBenefitted;
          else return y.WorkersBenefitted - x.WorkersBenefitted;
        });
        break;
      case 'Churches':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.Churches - y.Churches;
          else return y.Churches - x.Churches;
        });
        break;
      case 'Believers':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.Believers - y.Believers;
          else return y.Believers - x.Believers;
        });
        break;
      case 'Monthly Cost($US) per Worker':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.AmountPerWorker - y.AmountPerWorker;
          else return y.AmountPerWorker - x.AmountPerWorker;
        });
        break;
      case 'Year to Begin':
      case 'Year Begun':
        this.ethnics.sort(function (x: EthnicGroup, y: EthnicGroup) {
          if (!sortDesc) return x.Year - y.Year;
          else return y.Year - x.Year;
        });
        break;
    }
  }

  checkActions() {
    const currentHasBeenReviewed = this.allEthnics
      .filter((ethnic) => ethnic.Status === 'C')
      .filter((ethnic) => ethnic.Action.toLowerCase() === 'u');

    const futureHasBeenReviewed = this.allEthnics
      .filter((ethnic) => ethnic.Status === 'F')
      .filter((ethnic) => ethnic.Action.toLowerCase() === 'u');

    this.allCurrentEthnicGroupsReviewed = currentHasBeenReviewed.length <= 0;
    this.allFutureEthnicGroupsReviewed = futureHasBeenReviewed.length <= 0;

    // OLD LOGIC: Manual completion status setting - now handled by API
    // this.dropdownService.setEthnicTableToComplete(
    //   this.allCurrentEthnicGroupsReviewed,
    //   this.allFutureEthnicGroupsReviewed,
    // );
  }

  setEthnicGroupsToReviewed() {
    const unreviewedEthnics = this.ethnics.filter(
      (ethnic) => ethnic.Action === 'U',
    );

    if (unreviewedEthnics.length > 0) {
      // Collect all update observables
      const updateObservables = unreviewedEthnics.map((ethnic) => {
        ethnic.Action = 'R';
        return this.ethnicService.updateEthnicGroup(
          ethnic,
          ethnic.EthnicGroupID,
          this.router.url == '/current-ethnic-groups' ? 'Current' : 'future',
        );
      });

      // Wait for all updates to complete, then refresh completion status
      forkJoin(updateObservables).subscribe({
        next: () => {
          // Add a small delay to ensure backend has processed all updates
          setTimeout(() => {
            // Refresh completion status from API
            this.dropdownService.setPageCompletionStatuses();
            this.storePaginationState();
            // Refresh the data to update the component state without navigating
            // This prevents the navbar/sidebar from disappearing
            this.getData();
          }, 500);
        },
        error: (error) => {
          console.error('Error updating ethnic groups:', error);
          // Still try to refresh completion status even if some updates failed
          setTimeout(() => {
            this.dropdownService.setPageCompletionStatuses();
          }, 500);
        }
      });
    }
  }

  isValueEmpty(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'number' && isNaN(value))
    );
  }

  private cleanFormData(formData: any): any {
    const cleanedData = { ...formData };

    // Clean numeric fields to ensure raw numbers are sent to API
    const numericFields = ['Workers', 'WorkersBenefitted', 'Churches', 'Believers', 'AmountPerWorker', 'Year'];

    numericFields.forEach(field => {
      if (cleanedData[field] !== null && cleanedData[field] !== undefined) {
        const rawValue = NumberFormatter.getRawNumber(cleanedData[field]);
        cleanedData[field] = rawValue !== null ? rawValue : cleanedData[field];
      }
    });

    return cleanedData;
  }

  private initializeAutoSaveForEditing(ethnic: EthnicGroup) {
    const ethnicId = ethnic.EthnicGroupID!;
    const status = this.router.url == '/current-ethnic-groups' ? 'Current' : 'future';

    // Define editable fields and their form control names
    const editableFields = [
      'Name', 'Language', 'Location', 'Workers',
      'WorkersBenefitted', 'Churches', 'Believers',
      'AmountPerWorker', 'Year'
    ];

    editableFields.forEach(fieldName => {
      const fieldId = `ethnic-${ethnicId}-${fieldName}`;
      const formControl = this.ethnicGroupForm.get(fieldName) as FormControl;

      if (formControl) {
        const saveFunction = async (value: any) => {
          // Check if numeric field is blank before attempting save
          if (this.isNumericField(fieldName) && this.isValueEmpty(value)) {
            // Set error message for blank numeric field
            this.fieldErrors[fieldName] = `${this.getFieldDisplayName(fieldName)} field cannot be blank.`;
            return Promise.resolve(); // Don't save, but resolve to prevent error
          }

          // Clear error if field is valid
          if (this.isNumericField(fieldName) && !this.isValueEmpty(value)) {
            this.fieldErrors[fieldName] = null;
          }

          if (this.autoSaveService.validateField(formControl)) {
            // Clean numeric data if needed
            const cleanedValue = this.isNumericField(fieldName)
              ? NumberFormatter.getRawNumber(value) || value
              : value;

            return this.ethnicService.updateEthnicGroupField(
              ethnicId,
              fieldName,
              cleanedValue,
              status
            );
          }
          return Promise.resolve();
        };

        this.autoSaveService.initializeAutoSave(fieldId, saveFunction, {
          debounceMs: 500,
          validateField: true
        });
      }
    });
  }

  private isNumericField(fieldName: string): boolean {
    const numericFields = ['Workers', 'WorkersBenefitted', 'Churches', 'Believers', 'AmountPerWorker', 'Year'];
    return numericFields.includes(fieldName);
  }

  private getFieldDisplayName(fieldName: string): string {
    const fieldNameMap: { [key: string]: string } = {
      'Workers': 'Workers',
      'WorkersBenefitted': 'CAM Supported Workers',
      'Churches': 'Churches',
      'Believers': 'Believers',
      'AmountPerWorker': 'Monthly Cost per Worker',
      'Year': 'Year'
    };
    return fieldNameMap[fieldName] || fieldName;
  }

  onFieldBlur(fieldName: string) {
    if (this.itemToEdit && this.editState) {
      const ethnicId = this.itemToEdit.EthnicGroupID!;
      const fieldId = `ethnic-${ethnicId}-${fieldName}`;
      const formControl = this.ethnicGroupForm.get(fieldName) as FormControl;
      const value = formControl?.value;

      // For optional text fields, allow saving empty string as null
      if (fieldName === 'Language' || fieldName === 'Location') {
        const valueToSave = (value === '') ? null : value;
        this.autoSaveService.triggerSave(fieldId, valueToSave);
        // Clear any existing error for optional fields
        this.fieldErrors[fieldName] = null;
        return;
      }

      // Check if numeric field is blank
      if (this.isNumericField(fieldName) && this.isValueEmpty(value)) {
        // Set error message for blank numeric field
        this.fieldErrors[fieldName] = `${this.getFieldDisplayName(fieldName)} field cannot be blank.`;
        return; // Don't trigger save
      }

      // Clear error if field is valid
      if (this.isNumericField(fieldName) && !this.isValueEmpty(value)) {
        this.fieldErrors[fieldName] = null;
      }

      // For all other fields, skip saving when empty
      if (value !== null && value !== undefined && value !== '') {
        this.autoSaveService.triggerSave(fieldId, value);
      }
    }
  }

  ngOnDestroy() {
    // Clean up auto-save for all ethnic groups that were being edited
    this.ethnics.forEach(ethnic => {
      const ethnicId = ethnic.EthnicGroupID!;
      const editableFields = [
        'Name', 'Language', 'Location', 'Workers',
        'WorkersBenefitted', 'Churches', 'Believers',
        'AmountPerWorker', 'Year'
      ];

      editableFields.forEach(fieldName => {
        const fieldId = `ethnic-${ethnicId}-${fieldName}`;
        this.autoSaveService.cleanup(fieldId);
      });
    });
  }
}
